import { memo } from 'react';
import { Box, Flex, Skeleton } from '@chakra-ui/react';

import {
  PLAN_CARD_INNER_RADIUS,
  PLAN_CARD_PADDING,
  PLAN_CARD_TOOLBAR_PY,
  PLAN_CHART_VIEW_HEIGHT,
  PLAN_TABLE_CELL_BORDER,
  neutral,
} from '../planPageRevampUi';

interface Properties {
  fillHeight?: boolean;
  chartAreaHeight?: string;
}

const skeletonProperties = {
  startColor: 'gray.100',
  endColor: 'gray.200',
  borderRadius: '4px',
} as const;

/**
 * Loading state for the chart card — same shape as the real card (toolbar
 * strip, `p-16` inset bordered box, legend row) so the card doesn't resize
 * or lose its border when data lands. `PlanChartPanel` renders this inside
 * `PlanCard`, so this component only draws what's inside the card, not the
 * card itself.
 */
const PlanChartSkeleton = ({ fillHeight = false, chartAreaHeight }: Properties) => {
  const resolvedChartHeight = chartAreaHeight ?? PLAN_CHART_VIEW_HEIGHT;

  return (
    <>
      <Flex
        alignItems='center'
        justifyContent='space-between'
        gap={4}
        px={4}
        py={PLAN_CARD_TOOLBAR_PY}
        flexShrink={0}
        borderBottom='1px solid'
        borderColor={neutral.border}
      >
        <Skeleton height='14px' width='220px' {...skeletonProperties} />
        <Flex gap={3}>
          <Skeleton height='32px' width='32px' {...skeletonProperties} />
          <Skeleton height='32px' width='32px' {...skeletonProperties} />
          <Skeleton height='32px' width='120px' {...skeletonProperties} />
        </Flex>
      </Flex>

      <Box
        flex={fillHeight ? '1' : undefined}
        minH={fillHeight ? 0 : undefined}
        display='flex'
        flexDirection='column'
        p={PLAN_CARD_PADDING}
        flexShrink={0}
      >
        <Box
          flex={fillHeight ? '1' : undefined}
          h={fillHeight ? undefined : resolvedChartHeight}
          minH={fillHeight ? '200px' : resolvedChartHeight}
          border='1px solid'
          borderColor={PLAN_TABLE_CELL_BORDER}
          borderRadius={PLAN_CARD_INNER_RADIUS}
          p={4}
        >
          <Skeleton height='100%' minH='160px' {...skeletonProperties} />
        </Box>
      </Box>

      <Box px={4} pb={4} flexShrink={0}>
        <Flex gap={4} flexWrap='wrap'>
          {Array.from({ length: 4 }, (_, index) => (
            <Skeleton key={index} height='12px' width='96px' {...skeletonProperties} />
          ))}
        </Flex>
      </Box>
    </>
  );
};

export default memo(PlanChartSkeleton);
